import React, { memo, useCallback, useEffect, useRef, useState } from 'react'
import { Alert, Form, message, Modal, Skeleton } from 'antd'
import { useSelector } from 'react-redux'
import {
  getDeliveriesFormData,
  getDeliveriesFormError,
  getDeliveriesFormLoading,
  getVisibleFormData,
} from '../selectors'
import { HStack, VStack } from '../../../../ui/Stack'
import { DefaultJsonForm } from '../../../../components/v2/default-json-form'
import { useSaveResult } from '../hooks/useSaveResult'
import { useNavigate } from 'react-router-dom'
import { getDeliveriesForm } from '../../../../store/deliveries/action-creators'
import { useAppDispatch } from '../../../../hooks/useAppDispatch/useAppDispatch'
import { useAutoSave } from '../../../../hooks/useAutoSave'

interface DeliveriesFormsProps {
  className?: string
  id: string | number
}

const formItemLayout = {
  labelCol: {
    xs: { span: 24 },
    sm: { span: 8 },
  },
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 24 },
  },
}

const amountFields = [
  'shipment_amount',
  'lift_amount',
  'size_amount',
  'delivery_outside_amount',
  'other_amount',
]

export const DeliveriesForms: React.FC<DeliveriesFormsProps> = memo((props: DeliveriesFormsProps) => {
  const { id } = props
  const [form] = Form.useForm()
  const deliveriesFormData = useSelector(getDeliveriesFormData)
  const deliveriesFormIsLoading = useSelector(getDeliveriesFormLoading)
  const deliveriesFormError = useSelector(getDeliveriesFormError)
  const visibleFormData = useSelector(getVisibleFormData)
  const { saveResult } = useSaveResult()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const formRef = useRef<typeof visibleFormData>()
  const pendingSaveRef = useRef<{
    isDeliveryDateChanged: boolean
    isShipmentDateChanged: boolean
    isReloadForm: boolean
  } | null>(null)
  const isSavingRef = useRef(false)
  const [changeCounter, setChangeCounter] = useState(0)

  useEffect(() => {
    if (visibleFormData) {
      formRef.current = visibleFormData
    }
  }, [visibleFormData])

  useEffect(() => {
    return () => {
      const activeElement = document.activeElement as HTMLElement
      if (activeElement && typeof activeElement.blur === 'function') {
        activeElement.blur()
      }

      setTimeout(() => {
        if (isSavingRef.current) {
          return
        }

        if (!formRef.current || typeof formRef.current === 'boolean') {
          return
        }

        if (pendingSaveRef.current) {
          isSavingRef.current = true
          void saveResult(id, formRef.current, deliveriesFormData?.stages?.current, false, undefined)
        }
      }, 100)
    }
  }, [id, saveResult, deliveriesFormData])

  const saveCase = useCallback(async (is_next_stage, is_done = undefined) => {
    const res:any = await saveResult(id, formRef.current, deliveriesFormData?.stages?.current, is_next_stage, is_done)

    if (res?.shiftResult?.shifted && res?.shiftResult?.shifted.length > 0) {
      Modal.info({
        title: 'Перенос очереди',
        content: (
          <div>
            {res.shiftResult.shifted.map((order) => (
              <div key={order.id}>
                <a
                  href={`/logistics/deliveries/${order.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <b>{order.order_reference}</b>
                </a>
                : перенесён на очередь {order.newSequence}
                {res.shiftResult.unapproved.some((u) => u.id === order.id) && (
                  <span>,<b> снята отметка о согласовании</b></span>
                )}
              </div>
            ))}
          </div>
        ),
        okText: 'ОК',
        width: 600,
      })
    }
  }, [id, deliveriesFormData, saveResult])

  const handleAutoSave = useCallback(async () => {
    if (isSavingRef.current) {
      return
    }

    if (!formRef.current || !pendingSaveRef.current) {
      return
    }

    const { isDeliveryDateChanged, isShipmentDateChanged, isReloadForm } = pendingSaveRef.current

    try {
      isSavingRef.current = true
      await saveCase(isDeliveryDateChanged || isShipmentDateChanged)

      if (isReloadForm) {
        setTimeout(() => dispatch(getDeliveriesForm(id)), 100)
      }

      pendingSaveRef.current = null
    } finally {
      isSavingRef.current = false
    }
  }, [saveCase, dispatch, id])

  useAutoSave({
    data: changeCounter,
    onSave: handleAutoSave,
    debounceMs: 300,
    enabled: changeCounter > 0,
    onBeforeUnload: false,
  })

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (isSavingRef.current) {
        return
      }

      if (!formRef.current || typeof formRef.current === 'boolean') {
        return
      }

      const textareas = document.querySelectorAll<HTMLTextAreaElement>('textarea')
      const inputs = document.querySelectorAll<HTMLInputElement>('input')

      let hasChanges = false

      textareas.forEach(textarea => {
        const name = textarea.name
        if (name && formRef.current && typeof formRef.current !== 'boolean') {
          const currentValue = formRef.current.resultObject[name]
          const domValue = textarea.value
          if (currentValue !== domValue) {
            formRef.current.resultObject[name] = domValue
            hasChanges = true
          }
        }
      })

      inputs.forEach(input => {
        const name = input.name
        if (name && formRef.current && typeof formRef.current !== 'boolean') {
          const currentValue = formRef.current.resultObject[name]
          const domValue = input.value
          if (currentValue !== domValue) {
            formRef.current.resultObject[name] = domValue
            hasChanges = true
          }
        }
      })

      if (hasChanges) {
        pendingSaveRef.current = {
          isDeliveryDateChanged: false,
          isShipmentDateChanged: false,
          isReloadForm: false
        }

        isSavingRef.current = true
        void saveCase(false, undefined)
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [saveCase])

  useEffect(() => {
    const redirectUrl = deliveriesFormData?.redirectUrl
    if (redirectUrl) {
      navigate(redirectUrl)
    }
  }, [deliveriesFormData])

  const onChangeForm = useCallback(
    async ({ formData }) => {
      if (!formRef.current || typeof formRef.current === 'boolean') {
        return
      }

      const currentResultObject = formRef.current.resultObject
      const isDeliveryDateChanged =
        formData.hasOwnProperty('delivery_date') &&
        (currentResultObject as { delivery_date: string })?.delivery_date !== formData.delivery_date &&
        deliveriesFormData?.stages?.current > 2
      const isShipmentDateChanged =
        formData.hasOwnProperty('shipment_date') &&
        (currentResultObject as { shipment_date: string })?.shipment_date !== formData.shipment_date &&
        deliveriesFormData?.stages?.current > 0
      const isSetResponsibleManager =
        formData.approval_mark &&
        (currentResultObject as { approval_mark: boolean })?.approval_mark !== formData.approval_mark
      const isPaymentFieldChanged = [...amountFields, 'delivery_amount'].some(
        field => formData[field] !== undefined && formData[field] !== currentResultObject[field]
      )

      if (isDeliveryDateChanged || isShipmentDateChanged) {
        const confirmed = await new Promise<boolean>((resolve) => {
          Modal.confirm({
            title: 'Предупреждение',
            content: `Изменение даты ${isDeliveryDateChanged ? 'доставки' : 'отгрузки'} вернет заказ на стадию "Новая". Продолжить?`,
            okText: 'Подтвердить',
            cancelText: 'Отмена',
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
          })
        })

        if (!confirmed) {
          return
        }
      }

      let updatedFormData = { ...formRef.current.resultObject, ...formData }

      let isReloadForm = isSetResponsibleManager || isShipmentDateChanged
      if (isPaymentFieldChanged && !(currentResultObject as { payment_verified: boolean })?.payment_verified) {
        const totalAmount = amountFields.reduce((sum, field) => {
          const value = Number(updatedFormData[field]) || 0
          return sum + value
        }, 0)
        const deliveryAmount = Number(updatedFormData.delivery_amount) || 0
        const hasExcessForAdditionals = updatedFormData.excess_for_additionals ? !!updatedFormData.excess_for_additionals.trim() : false
        const hasSizeAmount = !!updatedFormData.size_amount

        if (totalAmount === deliveryAmount && deliveryAmount > 0 && !(hasExcessForAdditionals && !hasSizeAmount)) {
          updatedFormData = { ...updatedFormData, payment_verified: true }
          message.success('Оплата автоматически проверена')
          isReloadForm = true
        }
      }

      formRef.current = {
        resultSchema: formRef.current.resultSchema,
        resultObject: updatedFormData
      }

      pendingSaveRef.current = {
        isDeliveryDateChanged,
        isShipmentDateChanged,
        isReloadForm
      }

      setChangeCounter(prev => prev + 1)
    },
    [id, deliveriesFormData, dispatch, changeCounter]
  )

  if (deliveriesFormError && !deliveriesFormIsLoading && !deliveriesFormData) {
    return (
      <HStack gap={'8'} className={'mapWrapper'}>
        При выполнении запроса возникла ошибка.
        Попробуйте перезагрузить страницу или открыть карточку повторно.
      </HStack>
    )
  }

  return (
    <HStack gap={'8'} max>
      <Form
        {...formItemLayout}
        layout='horizontal'
        form={form}
        scrollToFirstError={true}
        style={{ width: '100%' }}
      >
        {deliveriesFormError && !deliveriesFormIsLoading ? (
          <div className={'alertWrapper'}>
            <Alert
              message='При выполнении операции возникла ошибка:'
              showIcon
              type='error'
              description={deliveriesFormError}
            />
          </div>
        ) : null}
        {!deliveriesFormIsLoading ? (
          <VStack gap={'16'} max align={'stretch'} className={'deliveriesForm'}>
            {formRef.current && (
              <DefaultJsonForm
                formData={formRef.current.resultObject}
                schema={formRef.current.resultSchema}
                onChange={onChangeForm}
                orientation='horizontal'
              />
            )}
          </VStack>
        ) : (
          <Skeleton active />
        )}
      </Form>
    </HStack>
  )
})
